import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import RequestCard from '../components/RequestCard';
import LeaveForm from '../components/forms/LeaveForm';
import SicknessForm from '../components/forms/SicknessForm';
import InterupsiForm from '../components/forms/InterupsiForm';
import { authFetch } from '../services/authFetch';

const RequestPage = () => {
  const location = useLocation();
  const [activeForm, setActiveForm] = useState(null);
  const [viewingHistoricalRequest, setViewingHistoricalRequest] = useState(null);

  // Check if we're navigating from the activity view
  useEffect(() => {
    if (location.state && location.state.viewingHistoricalRequest) {
      setViewingHistoricalRequest(location.state.viewingHistoricalRequest);
    }
  }, [location]);

  const requestTypes = [
    {
      id: 'interupsi',
      title: 'Interupsi Kelas Teori',
      description: 'Permohonan ijin meninggalkan kelas sementara',
      icon: '⏱️',
      color: '#e8f1fd'
    },
    {
      id: 'tidak-masuk',
      title: 'Tidak Masuk',
      description: 'Permohonan ijin tidak masuk kelas atau sekolah',
      icon: '🏠',
      color: '#e6f8f5'
    },
  ];

  // Dynamic historical request data
  const [historicalRequests, setHistoricalRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [errorRequests, setErrorRequests] = useState(null);

  useEffect(() => {
    const fetchAllRequests = async () => {
      setLoadingRequests(true);
      setErrorRequests(null);
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';


        // Fetch all requests in parallel
        const [interupsiRes, tidakMasukRes] = await Promise.all([
          authFetch(`${baseUrl}api/interuption_permits`, { method: 'GET', headers: { 'Accept': 'application/json' } }),
          authFetch(`${baseUrl}api/absent_permits`, { method: 'GET', headers: { 'Accept': 'application/json' } }),
        ]);

        let interupsiJson = null;
        try {
          interupsiJson = await interupsiRes.clone().json();

        } catch (e) {

        }
        if (!interupsiRes.ok || !tidakMasukRes.ok) throw new Error('Gagal memuat riwayat pengajuan.');
        const [interupsi, tidakMasuk] = await Promise.all([
          interupsiRes.json(),
          tidakMasukRes.json(),
        ]);
        // Handle interupsi data which is wrapped in a 'data' property
        const interupsiData = interupsi && interupsi.data && Array.isArray(interupsi.data) ? interupsi.data : [];

        // Map and merge all requests into a single array for the table
        const mappedInterupsi = interupsiData.map(item => {
          // Map backend status to UI status
          let status = 'pending';
          let statusText = 'Menunggu';
          if (item.request_status === 'disetujui' || item.request_status === 'approved') {
            status = 'approved';
            statusText = 'Disetujui';
          } else if (item.request_status === 'ditolak' || item.request_status === 'rejected') {
            status = 'rejected';
            statusText = 'Ditolak';
          } else if (item.request_status === 'menunggu' || item.request_status === 'pending') {
            status = 'pending';
            statusText = 'Menunggu';
          }
          return {
            id: `interupsi-${item.id}`,
            type: 'Interupsi',
            date: item.created_at || '',
            status,
            statusText,
            formType: 'interupsi',
            data: {
              classType: item.current_activity || 'Teori', // Map current_activity to classType for the toggle buttons
              departureTime: item.leave_hour || '',
              returnTime: item.return_hour || '',
              purpose: item.purpose || '',
              destination: item.destination || '',
              responsible: item.pic || '',
              relationship: item.relationship || '',
              phoneNumber: item.pic_contact_number || '',
              ...item // keep all original fields for fallback
            }
          };
        });
        const mappedTidakMasuk = (Array.isArray(tidakMasuk) ? tidakMasuk : []).map(item => ({
          id: `tidakmasuk-${item.id}`,
          type: 'Tidak Masuk',
          date: item.created_at || item.from_date || '',
          status: item.status || 'pending',
          statusText: item.status === 'approved' ? 'Disetujui' : item.status === 'rejected' ? 'Ditolak' : 'Menunggu',
          formType: 'tidak-masuk',
          data: item
        }));
        // Combine and sort by date (descending)
        const allRequests = [...mappedInterupsi, ...mappedTidakMasuk].sort((a, b) => new Date(b.date) - new Date(a.date));

        setHistoricalRequests(allRequests);
      } catch (err) {
        setErrorRequests(err.message);
        setHistoricalRequests([]);
      } finally {
        setLoadingRequests(false);
      }
    };
    fetchAllRequests();
  }, []);

  const handleViewHistoricalRequest = (requestId) => {
    const request = historicalRequests.find(req => req.id === requestId);
    if (request) {
      setViewingHistoricalRequest(request);
    }
  };

  const renderForm = () => {
    // If viewing a historical request
    if (viewingHistoricalRequest) {
      const request = viewingHistoricalRequest;

      // Show the appropriate form with prefilled data and read-only
      switch (request.formType) {
        case 'interupsi':
          return (
            <InterupsiForm
              onClose={() => setViewingHistoricalRequest(null)}
              initialData={request.data}
              readOnly={true}
              status={request.status}
              statusText={request.statusText}
              isStudentForm={true}
            />
          );
        case 'tidak-masuk':
          return (
            <SicknessForm
              onClose={() => setViewingHistoricalRequest(null)}
              initialData={request.data}
              readOnly={true}
              status={request.status}
              statusText={request.statusText}
            />
          );
        default:
          return null;
      }
    }

    // For new form requests
    switch (activeForm) {
      case 'interupsi':
        return <InterupsiForm onClose={() => setActiveForm(null)} isStudentForm={true} />;
      case 'tidak-masuk':
        return <SicknessForm onClose={() => setActiveForm(null)} />;
      default:
        return (
          <FormPlaceholder>
            <h3>Formulir ini sedang dalam pengembangan</h3>
            <p>Silakan coba kembali nanti.</p>
            <button onClick={() => setActiveForm(null)}>Kembali</button>
          </FormPlaceholder>
        );
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <h1>Permohonan Ijin</h1>
        <p>Pilih jenis ijin yang ingin diajukan</p>
      </PageHeader>

      {activeForm || viewingHistoricalRequest ? (
        <FormContainer>
          {renderForm()}
        </FormContainer>
      ) : (
        <>
          <RequestGrid>
            {requestTypes.map(request => (
              <RequestCard
                key={request.id}
                title={request.title}
                description={request.description}
                icon={request.icon}
                color={request.color}
                onClick={() => setActiveForm(request.id)}
              />
            ))}
          </RequestGrid>

          <RecentRequestsSection>
            <h2>Riwayat Pengajuan</h2>
            {loadingRequests ? (
              <LoadingMessage>Memuat riwayat pengajuan...</LoadingMessage>
            ) : errorRequests ? (
              <ErrorMessage>{errorRequests}</ErrorMessage>
            ) : historicalRequests.length === 0 ? (
              <EmptyMessage>Belum ada ijin yang diajukan.</EmptyMessage>
            ) : (
              <RequestTable>
                <thead>
                  <tr>
                    <th>Jenis Surat</th>
                    <th>Tanggal Pengajuan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {historicalRequests.map(request => {
                    // Format date to '9 Jun 2025'
                    let formattedDate = '';
                    if (request.date) {
                      const dateObj = new Date(request.date);
                      if (!isNaN(dateObj.getTime())) {
                        formattedDate = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }).format(dateObj);
                      } else {
                        formattedDate = request.date;
                      }
                    }
                    return (
                      <tr key={request.id}>
                        <td>{request.type}</td>
                        <td>{formattedDate}</td>
                        <td><StatusBadge status={request.status}>{request.statusText}</StatusBadge></td>
                        <td><ActionButton onClick={() => handleViewHistoricalRequest(request.id)}>Lihat</ActionButton></td>
                      </tr>
                    );
                  })}
                </tbody>
              </RequestTable>
            )}
          </RecentRequestsSection>
        </>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
  }
`;

const RequestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const RecentRequestsSection = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: var(--text-dark);
  }
`;

const RequestTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }
  
  th {
    font-weight: 600;
    color: var(--text-dark);
    background-color: var(--background-light);
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    
    th, td {
      padding: 0.75rem;
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  
  ${props => {
    switch (props.status) {
      case 'approved':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'pending':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'rejected':
        return `
          background-color: #feeeee;
          color: #dc3545;
        `;
      default:
        return '';
    }
  }}
`;

const ActionButton = styled.button`
  background-color: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  border-radius: 4px;
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const FormContainer = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
`;

const FormPlaceholder = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  
  h3 {
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
    margin-bottom: 1.5rem;
  }
`;

const LoadingMessage = styled.div`
  padding: 2rem 0;
  text-align: center;
  color: var(--text-light);
  font-size: 1rem;
`;

const ErrorMessage = styled.div`
  padding: 1.5rem;
  background-color: ${props => props.theme === 'dark' ? '#3a1c1c' : '#feeeee'};
  color: ${props => props.theme === 'dark' ? '#ff6b6b' : '#dc3545'};
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  
  &:before {
    content: "⚠️";
    margin-right: 0.75rem;
  }
`;

const EmptyMessage = styled.div`
  padding: 2rem 0;
  text-align: center;
  color: var(--text-light);
  font-style: italic;
  font-size: 0.95rem;
`;

export default RequestPage;